import React, { useEffect, useState } from "react"
import { Stack } from "react-bootstrap"
import CStack from "../../components/CStack"
import SwitchControl from "../../components/SwitchControl"
import OTPDialog from "../../containers/Auth/OTPDialog"
import usePasswordConfirmation from "../../hooks/usePasswordConfirmation"
import useUXStore from "../../api/ux/store"
import { useAuth } from "../../api/auth/store"
import { requestCrossBrowseOtp, toggleCrossCompanyBrowse } from "../../api/companies/actions"
import { CORE1_COM, OWNER_ROLE } from "../../constants"

// Cross-company browsing lets CoreONE administrators list the users of any company.
// It widens visibility across every tenant, so unlike the other company settings the
// toggle itself is gated behind the owner's password and a one-time code.

const errorMessage = (code: string): string => {
    switch (code) {
        case "invalid_otp":
            return "That code is incorrect or has expired."
        case "invalid_token":
            return "This request expired. Please start again."
        case "rate_limit_exceeded":
            return "Too many attempts. Wait a few minutes and try again."
        case "pass_invalid":
            return "That password is incorrect."
        case "unauthorized":
            return "Only a CoreONE owner can change this setting."
        default:
            return "Could not update the setting. Please try again."
    }
}

const CompanyCrossBrowse = () => {
    const { profile, retrieveCompanyProfile, hasRole, currentCompany } = useAuth(state => state)
    const { addToast } = useUXStore(state => state)
    const { confirm: confirmPassword } = usePasswordConfirmation()

    const [enabled, setEnabled] = useState(profile?.cross_com_browse || false)
    const [loading, setLoading] = useState(false)
    const [token, setToken] = useState<string | null>(null)
    const [pending, setPending] = useState(false)

    useEffect(() => {
        if (!profile) {
            retrieveCompanyProfile()
            return
        }
        setEnabled(profile.cross_com_browse || false)
    }, [profile, retrieveCompanyProfile])

    // Owner only, and only on core1. The server enforces both again.
    const canEdit = currentCompany === CORE1_COM && hasRole(OWNER_ROLE)

    const handleToggle = () => {
        const next = !enabled
        confirmPassword(async (password: string) => {
            try {
                const issued = await requestCrossBrowseOtp(next, password)
                setToken(issued)
                setPending(next)
            } catch (err) {
                throw new Error(errorMessage((err as Error).message))
            }
        }, {
            title: next ? "Enable cross-company browsing" : "Disable cross-company browsing",
            message: next
                ? "Re-enter your password to let CoreONE administrators view users of every company."
                : "Re-enter your password to stop CoreONE administrators viewing users of other companies."
        })
    }

    const handleVerify = async (otp: string) => {
        if (!token) throw new Error("This request expired. Please start again.")
        setLoading(true)
        try {
            await toggleCrossCompanyBrowse(token, otp)
        } catch (err) {
            throw new Error(errorMessage((err as Error).message))
        } finally {
            setLoading(false)
        }

        setToken(null)
        setEnabled(pending)
        await retrieveCompanyProfile()
        addToast("Success", `Cross-company browsing ${pending ? "enabled" : "disabled"}.`)
    }

    const handleResend = async () => {
        try {
            const next = await requestCrossBrowseOtp(pending, undefined, token || undefined)
            setToken(next)
        } catch (err) {
            throw new Error(errorMessage((err as Error).message))
        }
    }

    return (
        <>
            {token && (
                <OTPDialog
                    mode="dialog"
                    show
                    title="Verify this change"
                    description="Enter the 6-digit code sent to your email."
                    onVerify={handleVerify}
                    onResend={handleResend}
                    onCancel={() => {
                        setToken(null)
                        addToast("", "Change cancelled.")
                    }}
                    autoFocus
                    length={6}
                    resendCooldown={60}
                    expirationTime={300}
                />
            )}
            <CStack header="">
                <SwitchControl
                    text="Cross-Company User Browsing"
                    caption="Let CoreONE administrators view users from other companies."
                    value={enabled}
                    onChanged={handleToggle}
                    edit={!loading && canEdit}
                />
                <div className="mt-2 p-3 bg-light border-start border-primary border-4 rounded-end small text-secondary">
                    <Stack direction="horizontal" gap={2} className="align-items-start">
                        <div className="text-primary mt-1">
                            <i className="bi bi-shield-check"></i>
                        </div>
                        <div>
                            {enabled
                                ? "CoreONE administrators can select another company on the Users page and view its users. The view is read-only; changing a user still requires switching to that company."
                                : "CoreONE administrators can only see users of their own company."}
                            <br />
                            Changing this setting requires your password and a one-time code.
                        </div>
                    </Stack>
                </div>
            </CStack>
        </>
    )
}

export default CompanyCrossBrowse
